import { useCallback, useState } from 'react'
import { useHead, useSeoMeta } from '@unhead/react'
import toast from 'react-hot-toast'
import {Copy, Info} from 'lucide-react'
import { evaluateExpression, formatResult } from '@/lib/calculator'
import { track } from '@/lib/analytics'

const ERROR = 'ERROR'

export default function Calculator() {
  useSeoMeta({
    title: 'Basic Math Calculator Online — Praxis Core',
    description: 'Free online basic math calculator with PEMDAS support and memory keys. Practice calculator skills and computation with the Praxis Core emulator.',
    ogImage: 'https://www.calculatorskills.com/og/calculator.jpg',
    twitterCard: 'summary_large_image',
  })
  useHead({
    link: [{ rel: 'canonical', href: 'https://www.calculatorskills.com/calculator' }],
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebApplication',
          name: 'Praxis Core Basic Math Calculator',
          url: 'https://www.calculatorskills.com/calculator',
          applicationCategory: 'EducationalApplication',
          operatingSystem: 'Any (Web)',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
          description: 'Free online basic math calculator with PEMDAS evaluation and memory keys.',
        }),
      },
    ],
  })

  // Expression shown in display while typing; result string after equals.
  const [display, setDisplay] = useState<string>('0')
  // The raw expression being built
  const [expression, setExpression] = useState<string>('')
  // After "=" is pressed, next number entry should clear
  const [justEvaluated, setJustEvaluated] = useState<boolean>(false)
  // Memory variable
  const [memory, setMemory] = useState<number>(0)
  const [hasMemory, setHasMemory] = useState<boolean>(false)

  const isError = display === ERROR

  const reset = useCallback(() => {
    setDisplay('0')
    setExpression('')
    setJustEvaluated(false)
  }, [])

  const appendToExpression = useCallback((token: string, isOperator: boolean) => {
    if (isError) reset()

    if (isOperator) {
      // Replace trailing operator if user hits another
      setJustEvaluated(false)
      setExpression((prev) => {
        const base = prev || (display !== ERROR ? display : '0')
        const last = base[base.length - 1]
        if (last && '+-*/'.includes(last) && '+-*/'.includes(token)) {
          return base.slice(0, -1) + token
        }
        return base + token
      })
      setDisplay(token === '*' ? '×' : token === '/' ? '÷' : token)
      return
    }

    // Number / decimal / parenthesis
    if (justEvaluated && /[0-9.]/.test(token)) {
      // Start a fresh expression with this digit
      setExpression(token)
      setDisplay(token === '.' ? '0.' : token)
      setJustEvaluated(false)
      return
    }

    setExpression((prev) => {
      const next = prev + token
      // Update display to the trailing operand (for visual feedback)
      const trailing = next.match(/(-?\d*\.?\d*)$/)?.[0] ?? next
      const trimmed = trailing === '' ? next.slice(-1) : trailing
      // Limit display to 8 chars (digits/decimal)
      const cleanDigits = (trimmed || '0').replace(/^-/, '').replace('.', '')
      if (cleanDigits.length > 8) {
        // Don't allow more digits in current operand
        return prev
      }
      setDisplay(trimmed === '' || trimmed === '-' ? token : trimmed)
      return next
    })
    setJustEvaluated(false)
  }, [display, isError, justEvaluated, reset])

  const handleDigit = (d: string) => appendToExpression(d, false)

  const handleOperator = (op: '+' | '-' | '*' | '/') => appendToExpression(op, true)

  const handleParen = (p: '(' | ')') => {
    if (isError) reset()
    setExpression((prev) => prev + p)
    setDisplay(p)
    setJustEvaluated(false)
  }

  const handleDecimal = () => {
    if (isError) reset()
    // Prevent multiple decimals in current operand
    const trailing = (expression.match(/(-?\d*\.?\d*)$/)?.[0]) ?? ''
    if (trailing.includes('.')) return
    if (trailing === '' || /[+\-*/(]$/.test(expression) || expression === '') {
      // Add a leading 0 before dot for visual clarity
      setExpression((prev) => prev + '0.')
      setDisplay('0.')
    } else {
      appendToExpression('.', false)
    }
  }

  const handleClear = () => {
    reset()
  }

  const handleBackspace = () => {
    if (isError) { reset(); return }
    setExpression((prev) => {
      if (!prev) return prev
      const next = prev.slice(0, -1)
      const trailing = next.match(/(-?\d*\.?\d*)$/)?.[0] || ''
      const last = next[next.length - 1]
      if (!next) {
        setDisplay('0')
      } else if (last && '+-*/()'.includes(last)) {
        setDisplay(last === '*' ? '×' : last === '/' ? '÷' : last)
      } else {
        setDisplay(trailing || '0')
      }
      return next
    })
  }

  const handleEquals = () => {
    if (!expression) return
    const result = evaluateExpression(expression)
    const formatted = formatResult(result)
    setDisplay(formatted)
    if (formatted === ERROR) {
      setExpression('')
    } else {
      setExpression(formatted)
    }
    setJustEvaluated(true)
    track('calculator_use', '/calculator', { op: 'equals', result: formatted })
  }

  const handleSqrt = () => {
    // Square root of currently displayed value
    if (isError) { reset(); return }
    const current = parseFloat(display.replace('×', '*').replace('÷', '/'))
    if (Number.isNaN(current)) return
    if (current < 0) {
      setDisplay(ERROR)
      setExpression('')
      return
    }
    const r = Math.sqrt(current)
    const formatted = formatResult(r)
    setDisplay(formatted)
    setExpression(formatted === ERROR ? '' : formatted)
    setJustEvaluated(true)
  }

  // Memory functions
  const handleMC = () => { setMemory(0); setHasMemory(false); toast('Memory cleared', { icon: '∅' }) }
  const handleMR = () => {
    if (isError) reset()
    const formatted = formatResult(memory)
    setDisplay(formatted)
    setExpression(formatted === ERROR ? '' : formatted)
    setJustEvaluated(true)
  }
  const handleMPlus = () => {
    if (isError) return
    const current = parseFloat(display.replace('×', '*').replace('÷', '/'))
    if (Number.isNaN(current)) return
    const next = memory + current
    setMemory(next)
    setHasMemory(true)
    toast(`M+ : ${formatResult(next)}`, { icon: '＋' })
  }

  const handleTransfer = async () => {
    try {
      await navigator.clipboard.writeText(display)
      toast.success('Copied to clipboard')
      track('calculator_use', '/calculator', { op: 'transfer', value: display })
    } catch {
      toast.error('Clipboard unavailable')
    }
  }

  return (
    <div className="bg-[#FAFAF7]">
      {/* HEADER */}
      <section className="border-b-2 border-[#0A0A0A]">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 pt-14 lg:pt-20 pb-12 lg:pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-10 h-[2px] bg-[#E63946]" />
                <p className="eyebrow">/ Tool — 4-Function PEMDAS</p>
              </div>
              <h1 className="headline text-4xl sm:text-5xl lg:text-7xl mb-6">
                The Praxis Core<br/>
                <span className="text-[#E63946]">Calculator Emulator.</span>
              </h1>
              <p className="text-lg text-[#475569] max-w-2xl leading-relaxed">
                A free, fully-functional basic math calculator online — strict order of operations (PEMDAS), three memory keys, 8-digit display, and a transfer-display button that mirrors official testing environments.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CALCULATOR + INFO */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* CALCULATOR CARD */}
          <div className="lg:col-span-6 lg:col-start-1">
            <div className="border-2 border-[#0A0A0A] bg-[#FAFAF7] p-6 lg:p-8 shadow-[8px_8px_0_#0A0A0A]">
              {/* DISPLAY */}
              <div className="bg-[#0A0A0A] text-[#FAFAF7] p-6 mb-5">
                <div className="flex items-center justify-between mb-2 font-mono text-[10px] tracking-widest opacity-60">
                  <span>{hasMemory ? 'M' : ''}&nbsp;</span>
                  <span>4-FN · PEMDAS · 8-DIGIT</span>
                </div>
                <div className="font-mono text-[11px] text-[#FAFAF7]/40 truncate min-h-[14px]" aria-label="Expression">
                  {expression && expression !== display
                    ? expression.replace(/\*/g, '×').replace(/\//g, '÷')
                    : ' '}
                </div>
                <div
                  className={`font-mono text-right text-4xl sm:text-5xl font-medium tracking-tight ${isError ? 'text-[#E63946]' : ''}`}
                  aria-live="polite"
                  role="status"
                >
                  {display}
                </div>
              </div>

              {/* MEMORY ROW */}
              <div className="grid grid-cols-4 gap-2 mb-2">
                <button className="calc-btn mem" onClick={handleMC}>MC</button>
                <button className="calc-btn mem" onClick={handleMR}>MR</button>
                <button className="calc-btn mem" onClick={handleMPlus}>M+</button>
                <button className="calc-btn mem" onClick={handleSqrt}>√</button>
              </div>

              {/* CONTROL ROW */}
              <div className="grid grid-cols-4 gap-2 mb-2">
                <button className="calc-btn op" onClick={handleClear}>AC</button>
                <button className="calc-btn op" onClick={handleBackspace}>⌫</button>
                <button className="calc-btn" onClick={() => handleParen('(')}>(</button>
                <button className="calc-btn" onClick={() => handleParen(')')}>)</button>
              </div>

              {/* MAIN GRID */}
              <div className="grid grid-cols-4 gap-2">
                <button className="calc-btn" onClick={() => handleDigit('7')}>7</button>
                <button className="calc-btn" onClick={() => handleDigit('8')}>8</button>
                <button className="calc-btn" onClick={() => handleDigit('9')}>9</button>
                <button className="calc-btn op" onClick={() => handleOperator('/')}>÷</button>

                <button className="calc-btn" onClick={() => handleDigit('4')}>4</button>
                <button className="calc-btn" onClick={() => handleDigit('5')}>5</button>
                <button className="calc-btn" onClick={() => handleDigit('6')}>6</button>
                <button className="calc-btn op" onClick={() => handleOperator('*')}>×</button>

                <button className="calc-btn" onClick={() => handleDigit('1')}>1</button>
                <button className="calc-btn" onClick={() => handleDigit('2')}>2</button>
                <button className="calc-btn" onClick={() => handleDigit('3')}>3</button>
                <button className="calc-btn op" onClick={() => handleOperator('-')}>−</button>

                <button className="calc-btn col-span-2" onClick={() => handleDigit('0')}>0</button>
                <button className="calc-btn" onClick={handleDecimal}>.</button>
                <button className="calc-btn op" onClick={() => handleOperator('+')}>+</button>

                <button className="calc-btn eq col-span-4" onClick={handleEquals}>=</button>
              </div>
            </div>

            {/* TRANSFER */}
            <button
              onClick={handleTransfer}
              className="mt-5 w-full bg-[#0A0A0A] hover:bg-[#E63946] text-[#FAFAF7] py-4 font-semibold tracking-tight transition-colors flex items-center justify-center gap-2"
            >
              <Copy size={16} />
              Transfer Display to Clipboard
            </button>
          </div>

          {/* INFO PANEL */}
          <aside className="lg:col-span-6 lg:col-start-7">
            <div className="border-2 border-[#0A0A0A] p-8 lg:p-10 bg-[#FAFAF7]">
              <div className="flex items-center gap-2 mb-4">
                <Info size={18} />
                <p className="eyebrow">How to use</p>
              </div>
              <h2 className="headline text-3xl lg:text-4xl mb-6">
                How to Use This<br/>
                <span className="text-[#E63946]">Praxis Core Calculator.</span>
              </h2>
              <div className="space-y-6 text-[15px] leading-relaxed text-[#475569]">
                <div>
                  <h3 className="font-bold text-[#0A0A0A] mb-1.5 tracking-tight">PEMDAS order of operations</h3>
                  <p>
                    The calculator strictly follows the standard mathematical order of operations: Parentheses, Exponents, Multiplication and Division (left-to-right), then Addition and Subtraction (left-to-right). Entering <span className="font-mono text-[#0A0A0A]">1 + 2 × 4</span> yields <span className="font-mono text-[#0A0A0A]">9</span>, not <span className="font-mono">12</span>. Use parentheses to override the default order.
                  </p>
                </div>

                <div>
                  <h3 className="font-bold text-[#0A0A0A] mb-1.5 tracking-tight">Memory functions (MC · MR · M+)</h3>
                  <p>
                    <span className="font-mono text-[#0A0A0A]">M+</span> adds the current displayed number to a hidden memory variable. <span className="font-mono text-[#0A0A0A]">MR</span> recalls that memory value to the screen. <span className="font-mono text-[#0A0A0A]">MC</span> resets the memory variable back to zero. The "M" indicator appears on the display when memory holds a non-zero value.
                  </p>
                </div>

                <div>
                  <h3 className="font-bold text-[#0A0A0A] mb-1.5 tracking-tight">Display limits & error handling</h3>
                  <p>
                    The screen is limited to 8 digits. Any computation that produces a result greater than <span className="font-mono">99,999,999</span>, a division by zero, or the square root of a negative number will display <span className="font-mono text-[#E63946]">ERROR</span>. Press <span className="font-mono text-[#0A0A0A]">AC</span> to clear and start fresh.
                  </p>
                </div>

                <div>
                  <h3 className="font-bold text-[#0A0A0A] mb-1.5 tracking-tight">Transfer Display</h3>
                  <p>
                    The Transfer Display button copies the current screen value to your device clipboard, mimicking the digital answer boxes of standardized testing environments.
                  </p>
                </div>

                <div className="bg-[#0A0A0A] text-[#FAFAF7] p-5 -mx-2">
                  <p className="font-mono text-[11px] tracking-widest text-[#E63946] mb-2">/ TESTING TIP</p>
                  <p className="text-[#FAFAF7]/80 text-sm leading-relaxed">
                    Per official testing guidelines, do not rely on the calculator for simple mental math. Reaching for the device costs roughly 12 seconds per question — time better spent on problems that genuinely require computation.
                  </p>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  )
}
